'use client';

/**
 * Site-wide runtime configuration — fetched once on app mount from
 * `GET /api/public/config`.
 *
 * Consumers read it via `useSiteConfig()` and should tolerate the "loading"
 * state gracefully by falling back to sensible defaults. For example, the
 * login page renders the Google button during the first paint (defaults
 * assume it's enabled), then re-renders without it if the config says so.
 * This avoids an awkward flash where the button appears disabled.
 *
 * This provider does NOT block the UI — children render immediately with
 * defaults, then update once the fetch completes.
 */
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { getApiBaseUrl } from '@/lib/api-url';

// Keep in sync with the backend registry in `utils/site_settings.py`.
export interface SiteConfig {
  'auth.google_login_enabled': boolean;
  'auth.signup_enabled': boolean;
  'site.maintenance_mode': boolean;
  'site.name': string;
  'site.tagline': string;
  'hero.title_before': string;
  'hero.title_em': string;
  'hero.title_after': string;
  'hero.tagline': string;
  'ads.adsense_enabled': boolean;
  'ads.adsense_publisher_id': string;
  // Contact + social — surfaced in Footer, security.txt, JSON-LD, legal pages.
  'contact.security_email': string;
  'contact.support_email': string;
  'social.twitter_handle': string;
  'social.twitter_url': string;
  'social.github_url': string;
  'social.linkedin_url': string;
  'social.instagram_url': string;
  'social.youtube_url': string;
  // Analytics (Google Tag / GA4 / GTM) — public so the bootstrap script
  // can decide without a round-trip.
  'analytics.gtag_enabled': boolean;
  'analytics.gtag_id': string;
  'analytics.gtm_id': string;
  // reCAPTCHA v3 (only the *site* key is public; secret + threshold are admin-only).
  'recaptcha.enabled': boolean;
  'recaptcha.site_key': string;
  // Public-comments master switch. When false, the composer hides and the
  // backend rejects new comments + edits with 403. Existing threads stay readable.
  'comments.enabled': boolean;
  // User-filed reports master switch. When false, the Report menu hides
  // and POST /api/reports returns 403. Admin moderation continues normally.
  'reports.enabled': boolean;
  // Subscriptions (Pro plans) master switch. When false, /pricing 404s,
  // the admin Subscriptions tab is hidden, and the public/me APIs return 404.
  'subscriptions.enabled': boolean;
}

// Defaults shipped with the bundle. Used for first paint and whenever the
// config fetch is in flight or fails. If you change the backend defaults,
// update these too — otherwise you'll briefly render stale copy.
export const DEFAULT_SITE_CONFIG: SiteConfig = {
  'auth.google_login_enabled': true,
  'auth.signup_enabled': true,
  'site.maintenance_mode': false,
  'site.name': 'Prompt Save',
  'site.tagline': 'A gallery of great prompts',
  'hero.title_before': 'The art of the',
  'hero.title_em': 'perfect',
  'hero.title_after': 'prompt.',
  'hero.tagline': 'A curated gallery for creators, engineers, and writers to discover, save, and remix the prompts that actually work.',
  'ads.adsense_enabled': false,
  'ads.adsense_publisher_id': '',
  'contact.security_email': '',
  'contact.support_email': '',
  'social.twitter_handle': '',
  'social.twitter_url': '',
  'social.github_url': '',
  'social.linkedin_url': '',
  'social.instagram_url': '',
  'social.youtube_url': '',
  'analytics.gtag_enabled': false,
  'analytics.gtag_id': '',
  'analytics.gtm_id': '',
  'recaptcha.enabled': false,
  'recaptcha.site_key': '',
  'comments.enabled': true,
  'reports.enabled': true,
  'subscriptions.enabled': false,
};

interface SiteConfigContextType {
  config: SiteConfig;
  ready: boolean;
  refresh: () => Promise<void>;
}

const SiteConfigContext = createContext<SiteConfigContextType>({
  config: DEFAULT_SITE_CONFIG,
  ready: false,
  refresh: async () => {},
});

const BASE_URL = getApiBaseUrl();

export function SiteConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<SiteConfig>(DEFAULT_SITE_CONFIG);
  const [ready, setReady] = useState(false);

  const refresh = async () => {
    try {
      const res = await fetch(`${BASE_URL}/public/config`);
      if (!res.ok) throw new Error('config fetch failed');
      const data = (await res.json()) as Partial<SiteConfig>;
      // Merge over defaults so if the backend adds a new key before we ship
      // an updated bundle, we don't crash — missing keys fall back to defaults.
      setConfig({ ...DEFAULT_SITE_CONFIG, ...data });
    } catch {
      // Keep defaults on failure — better than a broken page.
    } finally {
      setReady(true);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <SiteConfigContext.Provider value={{ config, ready, refresh }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}
